import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { AccountingSyncChip } from '@/components/AccountingSyncChip'
import {
  formatRecognitionMonth,
  RecognitionInvoiceDrillDown,
  RecognitionScheduleWaterfall,
  type RecognitionDrillDown,
  type RecognitionWaterfall,
} from '@/components/RecognitionScheduleWaterfall'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { getJSON } from '@/lib/api'
import { formatMoneyCents } from '@/lib/money'
import { ViewOnlyBadge } from '@/components/templates/app-shell'

interface RecognitionScheduleResponse {
  recognition_schedule: {
    booked_from: string
    booked_to: string
    currency: string
    months: Array<{
      month: string
      recognize_this_month: string
      uncollected: string
      deferred_balance_after: string
    }>
    waterfall: RecognitionWaterfall
  }
}

interface RangeSelection {
  startsOn: string
  endsOn: string
}

export function RecognitionSchedulePage({
  currentAssociationDate,
  onOpenInvoice,
}: {
  currentAssociationDate: string
  onOpenInvoice: (invoiceId: number) => void
}) {
  const currentMonth = currentAssociationDate.slice(0, 7)
  const [form, setForm] = useState({ startsOn: currentMonth, endsOn: currentMonth })
  const [selection, setSelection] = useState<RangeSelection>({
    startsOn: currentMonth,
    endsOn: currentMonth,
  })
  const [drillDown, setDrillDown] = useState<RecognitionDrillDown | null>(null)
  const path = recognitionSchedulePath(selection)
  const query = useQuery({
    queryKey: ['recognition-schedule', selection.startsOn, selection.endsOn],
    queryFn: () => getJSON<RecognitionScheduleResponse>(path),
    retry: false,
  })
  const waterfall = query.data?.recognition_schedule.waterfall

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header>
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold">Recognition schedule</h1>
          <ViewOnlyBadge />
        </div>
        <p className="text-caption text-muted-foreground">
          Membership revenue by month earned · issued basis
        </p>
      </header>
      <form
        className="mt-4 flex flex-wrap items-end justify-between gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setSelection(form)
        }}
      >
        <div className="flex flex-wrap items-end gap-3">
          <MonthField
            label="Booked from"
            value={form.startsOn}
            onChange={(startsOn) => setForm({ ...form, startsOn })}
          />
          <MonthField
            label="through"
            value={form.endsOn}
            onChange={(endsOn) => setForm({ ...form, endsOn })}
          />
          <Button size="sm" type="submit">
            Apply
          </Button>
        </div>
        <Button size="sm" variant="outline" asChild>
          <a href={recognitionSchedulePath(selection, true)} download>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </a>
        </Button>
      </form>
      <div className="mt-4 mb-2 flex items-center justify-between">
        <p className="text-caption text-muted-foreground">
          {waterfall
            ? `${waterfall.invoice_count} ${waterfall.invoice_count === 1 ? 'invoice' : 'invoices'} scheduled`
            : '— invoices scheduled'}
        </p>
        <AccountingSyncChip />
      </div>
      <RecognitionScheduleBody query={query} onDrillDown={setDrillDown} />
      <RecognitionInvoiceDrillDown
        drillDown={drillDown}
        onOpenChange={(open) => !open && setDrillDown(null)}
        onOpenInvoice={onOpenInvoice}
      />
    </div>
  )
}

function MonthField({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (value: string) => void
}) {
  const id = `recognition-schedule-${label.replace(' ', '-').toLowerCase()}`
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="h-9 w-40"
        type="month"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  )
}

function RecognitionScheduleBody({
  query,
  onDrillDown,
}: {
  query: UseQueryResult<RecognitionScheduleResponse, Error>
  onDrillDown: (drillDown: RecognitionDrillDown) => void
}) {
  if (query.isLoading) {
    return <EmptyState title="Loading Recognition Schedule" className="min-h-52" />
  }
  if (query.isError) {
    return <EmptyState title="Recognition Schedule could not be loaded" className="min-h-52" />
  }

  const schedule = query.data?.recognition_schedule
  if (!schedule || schedule.waterfall.invoice_count === 0) {
    return (
      <EmptyState
        className="mt-2 min-h-52"
        variant="quiet"
        title="Nothing to recognize."
        description="Membership invoices appear here when issued."
      />
    )
  }

  const journalMonth = schedule.months.find((month) => month.month === schedule.waterfall.as_of)
  return (
    <>
      <JournalStrip
        asOf={schedule.waterfall.as_of}
        recognized={journalMonth?.recognize_this_month ?? formatMoneyCents(0, schedule.currency)}
        uncollected={journalMonth?.uncollected ?? formatMoneyCents(0, schedule.currency)}
        deferred={journalMonth?.deferred_balance_after ?? schedule.waterfall.total.remaining}
      />
      <RecognitionScheduleWaterfall waterfall={schedule.waterfall} onDrillDown={onDrillDown} />
    </>
  )
}

function JournalStrip({
  asOf,
  recognized,
  uncollected,
  deferred,
}: {
  asOf: string
  recognized: string
  uncollected: string
  deferred: string
}) {
  return (
    <Card className="mt-4 flex divide-x p-4">
      <JournalFigure label={`Recognize in ${formatRecognitionMonth(asOf)}`} value={recognized} />
      <JournalFigure label="Of which uncollected" value={uncollected} />
      <JournalFigure
        label={`Deferred balance after ${formatRecognitionMonth(asOf)}`}
        value={deferred}
      />
    </Card>
  )
}

function JournalFigure({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 px-4 first:pl-0">
      <div className="text-caption text-muted-foreground">{label}</div>
      <div className="text-body font-semibold tabular-nums">{value}</div>
    </div>
  )
}

function recognitionSchedulePath(selection: RangeSelection, exportCsv = false) {
  const suffix = exportCsv ? '/export' : ''
  return `/api/v1/recognition_schedule${suffix}?starts_on=${selection.startsOn}-01&ends_on=${monthEnd(selection.endsOn)}`
}

function monthEnd(month: string) {
  const [year, monthNumber] = month.split('-').map(Number)
  return new Date(Date.UTC(year, monthNumber, 0)).toISOString().slice(0, 10)
}
